import {
  Body1,
  Body2,
  Body3,
  Box,
  Button,
  ButtonColorVariant,
  Col,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  FormFieldStatus,
  Grid,
  Join,
  JustifyContent,
  Link,
  Select,
  Space,
} from '@gousto-internal/citrus-react'
import React, { useState } from 'react'

import type { OrderDeliveryDay, OrderDetails } from '@library/api-customer-engagement-experience'

import type { SaveSlotSelection } from './OrderCard'

export type OrderCardPanelProps = {
  orderId: string
  details: OrderDetails
  hasSaveSlotError?: boolean
  hasSkipDeliveryError?: boolean
  isSaveSlotPending?: boolean
  isSkipDeliveryPending?: boolean
  onSaveSlotClick?: (orderId: string, selection: SaveSlotSelection) => void
  onSkipDeliveryClick?: (orderId: string) => void
}

const resolveInitialDay = (
  days: OrderDeliveryDay[],
  selectedDayId?: string,
): OrderDeliveryDay | undefined => days.find((day) => day.id === selectedDayId) ?? days[0]

const resolveInitialSlotId = (
  day: OrderDeliveryDay | undefined,
  selectedSlotId?: string,
): string => {
  if (!day) return ''
  return day.slots.find((slot) => slot.id === selectedSlotId)?.id ?? day.slots[0]?.id ?? ''
}

export const OrderCardPanel = ({
  orderId,
  details,
  hasSaveSlotError = false,
  hasSkipDeliveryError = false,
  isSaveSlotPending = false,
  isSkipDeliveryPending = false,
  onSaveSlotClick,
  onSkipDeliveryClick,
}: OrderCardPanelProps) => {
  const days = details.available_delivery_days ?? []
  const canEditSlot = Boolean(details.edit_slot_action) && days.length > 0

  const initialDay = resolveInitialDay(days, details.selected_day_id)
  const [showEditDelivery, setShowEditDelivery] = useState(false)
  const [selectedDayId, setSelectedDayId] = useState<string>(initialDay?.id ?? '')
  const [selectedSlotId, setSelectedSlotId] = useState<string>(
    resolveInitialSlotId(initialDay, details.selected_slot_id),
  )

  const selectedDay = days.find((day) => day.id === selectedDayId) ?? initialDay

  // Only treat the selection as unchanged when the server told us what is currently
  // booked — without that we cannot know whether saving would be a no-op.
  const hasServerSelection = Boolean(details.selected_day_id && details.selected_slot_id)
  const isSelectionUnchanged =
    hasServerSelection &&
    selectedDayId === details.selected_day_id &&
    selectedSlotId === details.selected_slot_id

  const saveSlotErrorMessage = hasSaveSlotError ? details.edit_slot_action?.error_message : null
  const skipDeliveryErrorMessage = hasSkipDeliveryError ? details.skip_action?.error_message : null

  const handleEditToggleClick = () => {
    setShowEditDelivery((prev) => !prev)
  }

  const handleDayChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextDayId = event.target.value
    setSelectedDayId(nextDayId)
    const nextDay = days.find((day) => day.id === nextDayId)
    if (nextDay) {
      setSelectedSlotId(nextDay.slots[0]?.id ?? '')
    }
  }

  const handleSlotChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSlotId(event.target.value)
  }

  const handleSaveClick = () => {
    if (!selectedDay) return
    const slot = selectedDay.slots.find((s) => s.id === selectedSlotId)
    if (!slot) return
    onSaveSlotClick?.(orderId, {
      delivery_day_id: selectedDay.id,
      delivery_slot_id: slot.id,
      day_slot_lead_time_id: slot.day_slot_lead_time_id,
    })
  }

  const handleSkipClick = () => {
    onSkipDeliveryClick?.(orderId)
  }

  return (
    <Box data-testid={`order-card-panel-${orderId}`}>
      <Space size={6} direction="vertical" />
      <Grid width="100%">
        <Col size={[12, 6]}>
          <Join with={<Space size={3} direction="vertical" />}>
            <Body1 fontWeight={FontWeight.Bold} color={Color.ColdGrey_900}>
              {details.heading}
            </Body1>
            <Box>
              <Box
                display={Display.Flex}
                flexDirection={FlexDirection.Row}
                justifyContent={JustifyContent.SpaceBetween}
              >
                <Box>
                  <Body2 fontWeight={FontWeight.Bold} color={Color.ColdGrey_900}>
                    {details.delivery_date_label}
                  </Body2>
                </Box>
                {canEditSlot ? (
                  <Box>
                    <Link
                      onClick={handleEditToggleClick}
                      data-testid="order-card-panel-edit-toggle"
                    >
                      {!showEditDelivery ? 'Edit' : 'Close'}
                    </Link>
                  </Box>
                ) : null}
              </Box>
              <Body2 color={Color.ColdGrey_900}>{details.delivery_time_label}</Body2>
            </Box>
            {showEditDelivery && canEditSlot ? (
              <Box>
                <Join with={<Space size={3} direction="vertical" />}>
                  <Box
                    display={Display.Flex}
                    flexDirection={[FlexDirection.Column, FlexDirection.Row]}
                  >
                    <Join with={<Space size={3} direction="horizontal" />}>
                      <Box width={['100%', '50%']}>
                        <Select
                          id={`order-card-panel-day-${orderId}`}
                          data-testid="order-card-panel-day-select"
                          onChange={handleDayChange}
                          defaultValue={selectedDayId}
                          status={saveSlotErrorMessage ? FormFieldStatus.Error : undefined}
                          fullWidth
                        >
                          {days.map((day) => (
                            <option key={day.id} value={day.id}>
                              {day.formatted_date}
                            </option>
                          ))}
                        </Select>
                        <Space size={[3, 0]} direction="vertical" />
                      </Box>
                      <Box width={['100%', '50%']}>
                        <Select
                          key={selectedDayId}
                          id={`order-card-panel-slot-${orderId}`}
                          data-testid="order-card-panel-slot-select"
                          onChange={handleSlotChange}
                          defaultValue={selectedSlotId}
                          fullWidth
                        >
                          {(selectedDay?.slots ?? []).map((slot) => (
                            <option key={slot.id} value={slot.id}>
                              {slot.time_label}
                            </option>
                          ))}
                        </Select>
                      </Box>
                    </Join>
                  </Box>
                  <Box>
                    {saveSlotErrorMessage ? (
                      <>
                        <Body3
                          color={Color.Error_900}
                          data-testid="order-card-panel-save-slot-error"
                        >
                          {saveSlotErrorMessage}
                        </Body3>
                        <Space size={2} direction="vertical" />
                      </>
                    ) : null}
                    <Button
                      colorVariant={ButtonColorVariant.Secondary}
                      width="100%"
                      disabled={isSelectionUnchanged || isSaveSlotPending}
                      onClick={handleSaveClick}
                      data-testid="order-card-panel-save-slot"
                    >
                      {details.edit_slot_action?.title}
                    </Button>
                  </Box>
                </Join>
              </Box>
            ) : null}
          </Join>
          {details.delivery_address_label ? (
            <>
              <Space size={3} direction="vertical" />
              {details.delivery_address_title ? (
                <Body2 fontWeight={FontWeight.Bold} color={Color.ColdGrey_900}>
                  {details.delivery_address_title}
                </Body2>
              ) : null}
              <Body2 color={Color.ColdGrey_900}>{details.delivery_address_label}</Body2>
            </>
          ) : null}
          <Space size={3} direction="vertical" />
          {details.skip_action ? (
            <>
              {skipDeliveryErrorMessage ? (
                <>
                  <Body3 color={Color.Error_900} data-testid="order-card-panel-skip-error">
                    {skipDeliveryErrorMessage}
                  </Body3>
                  <Space size={2} direction="vertical" />
                </>
              ) : null}
              <Button
                colorVariant={ButtonColorVariant.Warning}
                width="100%"
                disabled={isSkipDeliveryPending}
                onClick={handleSkipClick}
                data-testid="order-card-panel-skip"
              >
                {details.skip_action.title}
              </Button>
            </>
          ) : null}
        </Col>
        <Col size={[12, 6]} display={[Display.None, Display.Flex]} />
      </Grid>
    </Box>
  )
}
